/**
 * Document-level UI preferences (W1-12 ui-kit-i18n). The settings service / shell calls `applyUiPreferences`
 * whenever the user settings change; the design system reads the result from attributes on <html>:
 *
 *   data-theme           'dark' (default) | 'light' | 'high-contrast' | 'system'   (themes.css)
 *   data-reduced-motion  'true' (reduce) | 'false' (animate) | absent (follow the OS)   (global.css)
 *   data-level           'guided' | 'full' (Full hides the Guided help lines)          (global.css)
 *   --ui-scale           chrome scale factor (tokens.css)
 *
 * The locale is mirrored into <html lang> by the i18n runtime itself (`i18n.setLocale`).
 */

export type UiTheme = 'dark' | 'light' | 'highContrast' | 'system';
export type UiReducedMotion = 'system' | 'on' | 'off';
export type UiLevel = 'guided' | 'full';

export interface UiPreferences {
  readonly theme?: UiTheme;
  readonly reducedMotion?: UiReducedMotion;
  /** 0.75 .. 1.5; 1 = 100 %. */
  readonly uiScale?: number;
  readonly level?: UiLevel;
}

export const UI_SCALE_MIN = 0.75;
export const UI_SCALE_MAX = 1.5;

/** Value of the `data-theme` attribute for a theme setting. */
export function themeAttribute(theme: UiTheme): string {
  return theme === 'highContrast' ? 'high-contrast' : theme;
}

/** Applies the given preferences to `root` (default: <html>); omitted fields are left unchanged. */
export function applyUiPreferences(prefs: UiPreferences, root: HTMLElement = document.documentElement): void {
  if (prefs.theme !== undefined) root.dataset.theme = themeAttribute(prefs.theme);
  if (prefs.reducedMotion !== undefined) {
    if (prefs.reducedMotion === 'system') delete root.dataset.reducedMotion;
    else root.dataset.reducedMotion = prefs.reducedMotion === 'on' ? 'true' : 'false';
  }
  if (prefs.uiScale !== undefined) {
    const scale = Number.isFinite(prefs.uiScale) ? Math.min(UI_SCALE_MAX, Math.max(UI_SCALE_MIN, prefs.uiScale)) : 1;
    root.style.setProperty('--ui-scale', String(scale));
  }
  if (prefs.level !== undefined) root.dataset.level = prefs.level;
}
